import { useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { BugIcon } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { format, set } from 'date-fns';
import { TFunction } from 'i18next';
import uniq from 'lodash/uniq';
import { ChevronDownIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

import {
  type NavItem,
  useNavigationItems,
} from '@/components/layout/navigation-panel/navigation-items';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';
import { useToast } from '@/services/toast';

function useZapperSchema() {
  const { t } = useTranslation('zapper');
  return useMemo(
    () =>
      z.object({
        reportType: z.enum(['BUG', 'FEATURE_REQUEST']),
        title: z.string().trim().min(1, t('errors.title_required')),
        description: z.string().trim().min(1, t('errors.description_required')),
        severity: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']),
        occurredAtDate: z.date({
          required_error: t('errors.occurred_at_required'),
        }),
        occurredAtTime: z
          .string()
          .regex(/^\d{2}:\d{2}$/, t('errors.occurred_at_required')),
        module: z.string().trim().min(1, t('errors.module_required')),
        userIntent: z.string().trim().min(1, t('errors.user_intent_required')),
        impact: z.string().trim().min(1, t('errors.impact_required')),
        affectsMultipleUsers: z.boolean(),
        reproductionSteps: z.string().optional(),
        expectedResult: z.string().optional(),
        actualResult: z.string().optional(),
      }),
    [t],
  );
}

type ZapperFormValues = z.infer<ReturnType<typeof useZapperSchema>>;

export function ZapperDialog() {
  const { t } = useTranslation('zapper');
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [occurredAtCalendarOpen, setOccurredAtCalendarOpen] = useState(false);
  const location = useLocation();
  const zapperSchema = useZapperSchema();
  const token = useAccessToken();
  const reportTypeItems = [
    { label: t('report_types.bug'), value: 'BUG' },
    { label: t('report_types.feature_request'), value: 'FEATURE_REQUEST' },
  ] satisfies Array<{
    label: React.ReactNode;
    value: ZapperFormValues['reportType'];
  }>;
  const severityItems = [
    { label: t('severities.low'), value: 'LOW' },
    { label: t('severities.medium'), value: 'MEDIUM' },
    { label: t('severities.high'), value: 'HIGH' },
    { label: t('severities.critical'), value: 'CRITICAL' },
  ] satisfies Array<{
    label: React.ReactNode;
    value: ZapperFormValues['severity'];
  }>;
  const navigationItems = useNavigationItems();
  const moduleItems = useMemo(
    () =>
      uniq(getNavigationRouteItems(navigationItems).map((item) => item.label)),
    [navigationItems],
  );
  const currentRouteModule = useMemo(
    () => getCurrentRouteModule(navigationItems, location.pathname),
    [navigationItems, location.pathname],
  );
  const submitReportMutation = useMutation({
    mutationFn: (values: ZapperFormValues) => submitReport(token, values, t),
  });
  const form = useForm<ZapperFormValues>({
    resolver: zodResolver(zapperSchema),
    defaultValues: buildDefaultValues(currentRouteModule),
  });

  const resetForm = () => {
    reset(buildDefaultValues(currentRouteModule));
  };

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    reset,
  } = form;

  const onSubmit = async (values: ZapperFormValues) => {
    submitReportMutation.reset();
    try {
      await submitReportMutation.mutateAsync(values);
    } catch {
      return;
    }

    toast.show({
      severity: 'success',
      summary: t('toast.success_summary'),
      detail: t('toast.success_detail'),
    });
    resetForm();
    setOpen(false);
  };

  const isSubmittingReport = isSubmitting || submitReportMutation.isPending;
  const serverError = submitReportMutation.error
    ? submitReportMutation.error.message
    : null;
  const handleCancel = () => {
    resetForm();
    submitReportMutation.reset();
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) resetForm();
      }}
    >
      <Tooltip>
        <DialogTrigger
          render={
            <TooltipTrigger>
              <BugIcon className="size-5" />
            </TooltipTrigger>
          }
        />
        <TooltipContent>{t('tooltip')}</TooltipContent>
      </Tooltip>
      <DialogContent className="md:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="zapper-report-form"
            onSubmit={handleSubmit(onSubmit)}
            className="grid gap-4 p-1"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Controller
                control={form.control}
                name="title"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>
                      {t('fields.title')}
                    </FieldLabel>
                    <Input
                      {...field}
                      id={field.name}
                      aria-invalid={fieldState.invalid}
                      placeholder={t('placeholders.title')}
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
              <Controller
                control={control}
                name="reportType"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>
                      {t('fields.report_type')}
                    </FieldLabel>
                    <Select
                      items={reportTypeItems}
                      ref={field.ref}
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                    >
                      <SelectTrigger
                        id={field.name}
                        className="w-full"
                        aria-invalid={fieldState.invalid}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {reportTypeItems.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Controller
                control={form.control}
                name="module"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>
                      {t('fields.module')}
                    </FieldLabel>
                    <Combobox
                      items={moduleItems}
                      value={field.value}
                      onValueChange={(item) => field.onChange(item ?? '')}
                      itemToStringValue={(item) => item}
                      itemToStringLabel={(item) => item}
                    >
                      <ComboboxInput
                        id={field.name}
                        ref={field.ref}
                        name={field.name}
                        className="w-full"
                        aria-invalid={fieldState.invalid}
                        placeholder={t('placeholders.module')}
                        showClear
                      />
                      <ComboboxContent>
                        <ComboboxEmpty>{t('module.empty')}</ComboboxEmpty>
                        <ComboboxList>
                          {(item: (typeof moduleItems)[number]) => (
                            <ComboboxItem key={item} value={item}>
                              {item}
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
              <Controller
                control={control}
                name="severity"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>
                      {t('fields.severity')}
                    </FieldLabel>
                    <Select
                      items={severityItems}
                      ref={field.ref}
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                    >
                      <SelectTrigger
                        id={field.name}
                        className="w-full"
                        aria-invalid={fieldState.invalid}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {severityItems.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
            </div>

            <FieldGroup className="grid gap-4 sm:grid-cols-2">
              <Controller
                control={form.control}
                name="occurredAtDate"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>
                      {t('fields.occurred_at')}
                    </FieldLabel>
                    <Popover
                      open={occurredAtCalendarOpen}
                      onOpenChange={setOccurredAtCalendarOpen}
                    >
                      <PopoverTrigger
                        render={
                          <Button
                            variant="outline"
                            id={field.name}
                            className="justify-between font-normal"
                            aria-invalid={fieldState.invalid}
                          />
                        }
                      >
                        {field.value
                          ? format(field.value, 'PPP')
                          : t('placeholders.select_date')}
                        <ChevronDownIcon data-icon="inline-end" />
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto overflow-hidden p-0"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={field.value}
                          captionLayout="dropdown"
                          defaultMonth={field.value}
                          onSelect={(date) => {
                            if (date) field.onChange(date);
                            setOccurredAtCalendarOpen(false);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
              <Controller
                control={form.control}
                name="occurredAtTime"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>
                      {t('fields.occurred_at_time')}
                    </FieldLabel>
                    <Input
                      {...field}
                      type="time"
                      id={field.name}
                      aria-invalid={fieldState.invalid}
                      className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
            </FieldGroup>

            <Controller
              control={form.control}
              name="description"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>
                    {t('fields.description')}
                  </FieldLabel>
                  <Textarea
                    {...field}
                    id={field.name}
                    aria-invalid={fieldState.invalid}
                    className="min-h-24"
                    placeholder={t('placeholders.description')}
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />
            <Controller
              control={form.control}
              name="userIntent"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>
                    {t('fields.user_intent')}
                  </FieldLabel>
                  <Textarea
                    {...field}
                    id={field.name}
                    aria-invalid={fieldState.invalid}
                    className="min-h-24"
                    placeholder={t('placeholders.user_intent')}
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />
            <Controller
              control={form.control}
              name="impact"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>
                    {t('fields.impact')}
                  </FieldLabel>
                  <Textarea
                    {...field}
                    id={field.name}
                    aria-invalid={fieldState.invalid}
                    className="min-h-24"
                    placeholder={t('placeholders.impact')}
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />
            <Controller
              control={form.control}
              name="affectsMultipleUsers"
              render={({ field, fieldState }) => (
                <Field
                  data-invalid={fieldState.invalid}
                  orientation="horizontal"
                  className="items-center"
                >
                  <Checkbox
                    id={field.name}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-invalid={fieldState.invalid}
                  />
                  <FieldLabel htmlFor={field.name}>
                    {t('fields.affects_multiple_users')}
                  </FieldLabel>
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />

            <FieldGroup className="grid gap-4 sm:grid-cols-3">
              <Controller
                control={form.control}
                name="reproductionSteps"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>
                      {t('fields.reproduction_steps')}
                    </FieldLabel>
                    <Textarea
                      {...field}
                      id={field.name}
                      aria-invalid={fieldState.invalid}
                      className="min-h-24"
                      placeholder={t('placeholders.reproduction_steps')}
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
              <Controller
                control={form.control}
                name="expectedResult"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>
                      {t('fields.expected_result')}
                    </FieldLabel>
                    <Textarea
                      {...field}
                      id={field.name}
                      aria-invalid={fieldState.invalid}
                      className="min-h-24"
                      placeholder={t('placeholders.expected_result')}
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
              <Controller
                control={form.control}
                name="actualResult"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>
                      {t('fields.actual_result')}
                    </FieldLabel>
                    <Textarea
                      {...field}
                      id={field.name}
                      aria-invalid={fieldState.invalid}
                      className="min-h-24"
                      placeholder={t('placeholders.actual_result')}
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
            </FieldGroup>

            {serverError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {serverError}
              </div>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmittingReport}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="zapper-report-form"
            disabled={isSubmittingReport}
          >
            {isSubmittingReport ? t('actions.submitting') : t('actions.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function buildDefaultValues(module: string): ZapperFormValues {
  const now = new Date();

  return {
    reportType: 'BUG',
    title: '',
    description: '',
    severity: 'LOW',
    occurredAtDate: now,
    occurredAtTime: format(now, 'HH:mm'),
    module,
    userIntent: '',
    impact: '',
    affectsMultipleUsers: false,
    reproductionSteps: '',
    expectedResult: '',
    actualResult: '',
  };
}

function getCurrentRouteModule(items: NavItem[], pathname: string) {
  const routeItems = getNavigationRouteItems(items);
  const exactMatch = routeItems.find(
    (item) => getNavigationItemPathname(item.path) === pathname,
  );
  const containingMatch = routeItems.find((item) =>
    isNavigationPathContained(item.path, pathname),
  );

  return exactMatch?.label ?? containingMatch?.label ?? '';
}

function getNavigationRouteItems(
  items: NavItem[],
  parentLabels: string[] = [],
): Array<{
  label: string;
  path: string;
}> {
  return items.flatMap((item) => {
    const labels = item.label ? [...parentLabels, item.label] : parentLabels;
    const currentItem =
      item.path && labels.length
        ? [{ label: labels.join(' - '), path: item.path }]
        : [];

    return [
      ...currentItem,
      ...(item.children ? getNavigationRouteItems(item.children, labels) : []),
    ];
  });
}

function isNavigationPathContained(navigationPath: string, pathname: string) {
  const navigationPathname = getNavigationItemPathname(navigationPath);

  if (navigationPathname === '/') {
    return pathname === '/';
  }

  return pathname.includes(`${navigationPathname}/`);
}

function getNavigationItemPathname(navigationPath: string) {
  const [rawPathname = ''] = navigationPath.split('?');
  return rawPathname;
}

function combineDateAndTime(date: Date, time: string) {
  const [hours = 0, minutes = 0] = time.split(':').map((part) => Number(part));

  return set(date, {
    hours,
    minutes,
    seconds: 0,
    milliseconds: 0,
  }).toISOString();
}

function emptyToUndefined(value?: string) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

async function submitReport(
  token: AccessToken,
  values: ZapperFormValues,
  t: TFunction<'zapper'>,
) {
  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/api/zapper`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({
        reportType: values.reportType,
        title: values.title,
        description: values.description,
        severity: values.severity,
        occurredAt: combineDateAndTime(
          values.occurredAtDate,
          values.occurredAtTime,
        ),
        userIntent: values.userIntent,
        impact: values.impact,
        affectsMultipleUsers: values.affectsMultipleUsers,
        module: values.module,
        reproductionSteps: emptyToUndefined(values.reproductionSteps),
        expectedResult: emptyToUndefined(values.expectedResult),
        actualResult: emptyToUndefined(values.actualResult),
        pageUrl: window.location.href,
        environment: import.meta.env.MODE,
        metadata: getReportMetadata(),
      }),
    },
  );

  if (!response.ok) {
    let message = t('errors.submit_failed');

    try {
      const problem = await response.json();
      const errors = problem?.errors as Record<string, string[]> | undefined;
      const firstError = errors ? Object.values(errors).flat()[0] : undefined;
      message = firstError || problem?.title || message;
    } catch {
      // Use the generic message when the response body is not JSON.
    }

    throw new Error(message);
  }
}

function getReportMetadata() {
  return {
    referrer: document.referrer || undefined,
    title: document.title,
    language: window.navigator.language,
    languages: window.navigator.languages,
    userAgent: window.navigator.userAgent,
    platform: window.navigator.platform,
    cookieEnabled: window.navigator.cookieEnabled,
    online: window.navigator.onLine,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
    },
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      availableWidth: window.screen.availWidth,
      availableHeight: window.screen.availHeight,
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    localTime: new Date().toISOString(),
    storage: {
      localStorage: readStorage(window.localStorage),
      sessionStorage: readStorage(window.sessionStorage),
    },
    historyLength: window.history.length,
  };
}

function readStorage(storage: Storage) {
  return Array.from({ length: storage.length }, (_, index) => {
    const key = storage.key(index);
    if (!key) return null;

    return [key, sanitizeMetadataValue(key, storage.getItem(key))] as const;
  }).reduce<Record<string, string | null>>((items, entry) => {
    if (entry) items[entry[0]] = entry[1];
    return items;
  }, {});
}

function sanitizeMetadataValue(key: string, value: string | null) {
  if (!value) return value;

  if (/(auth|bearer|credential|jwt|key|password|secret|token)/i.test(key))
    return '[redacted]';

  return value.length > 2000 ? `${value.slice(0, 2000)}...[truncated]` : value;
}
